import * as math from 'mathjs';
import {
  ColumnType,
  OrderColumnDeepFragment,
  QuantityEnum,
  QuantityOperation,
  QuantityTypeInput,
} from '../gql/graphql';

export type ColumnNameAndId = Pick<OrderColumnDeepFragment, 'name' | 'id'>;

export function stringifyDerivedFormula(
  value: string,
  columns: ColumnNameAndId[]
) {
  let formula = value;
  for (const column of columns) {
    formula = formula.replaceAll(
      columnIdToScopeVariable(column.id),
      columnNameToFormulaVariable(column.name)
    );
  }
  formula = formula.replace(/var_\w+/gi, 'undefined');
  return formula;
}

export function parseAndStringifyDerivedFormula(
  value: string,
  columns: ColumnNameAndId[]
) {
  try {
    return stringifyDerivedFormula(math.parse(value).toString(), columns);
  } catch (e) {
    console.error('Could not stringify formula');
    return value;
  }
}

export function parseDerivedFormula(value: string, columns: ColumnNameAndId[]) {
  let formula = value.replace(/\s+/g, ' '); //Remove duplicate whitespace
  const columnsByLengthDesc = columns.sort(
    (a, b) => b.name.length - a.name.length
  );
  for (const column of columnsByLengthDesc) {
    formula = formula.replaceAll(
      columnNameToFormulaVariable(column.name),
      columnIdToScopeVariable(column.id)
    );
  }
  return formula;
}

export function columnIdToScopeVariable(id: string) {
  return `var_${id.replaceAll('-', '_')}`;
}

export function columnNameToFormulaVariable(columnName: string) {
  return `$${columnName.replaceAll(' ', '_').toLowerCase()}`;
}

export function getVariableColor(index: number) {
  switch (index % 10) {
    case 0:
      return 'cyan';
    case 1:
      return 'orange';
    case 2:
      return 'green';
    case 3:
      return 'red';
    case 4:
      return 'yellow';
    case 5:
      return 'teal';
    case 6:
      return 'purple';
    case 7:
      return 'gray';
    case 8:
      return 'blue';
    default:
      return 'pink';
  }
}

export function getQuantityDisplayValue(quantity: QuantityTypeInput) {
  if (quantity.type === QuantityEnum.Static) {
    return quantity.value?.toString() ?? quantity.stringValue ?? '';
  } else if (
    quantity.type === QuantityEnum.Derived &&
    quantity.derivedFormula
  ) {
    const formula = quantity.derivedFormula.replaceAll('_endvar', '');
    return formula.replaceAll('var_', '');
  } else if (
    quantity.type === QuantityEnum.Dynamic &&
    quantity.calculation?.operation === QuantityOperation.Count
  ) {
    return 'Linked count';
  } else if (quantity.type === QuantityEnum.Dynamic) {
    return quantity.calculation?.attribute?.name ?? '';
  } else {
    return null;
  }
}

export type Substitution = {
  name: string;
  columnId: string;
  variableName: string;
};

const allowedColumnTypesInFormula = [
  ColumnType.Custom,
  ColumnType.LinkedCount,
  ColumnType.Quantity,
  ColumnType.UnitPrice,
];

export function getValidSubstitutions(
  columnId: string,
  orderColumns: OrderColumnDeepFragment[]
): Substitution[] {
  return orderColumns
    .filter((column) => allowedColumnTypesInFormula.includes(column.columnType))
    .filter((column) => column.id !== columnId)
    .map((column) => ({
      name: column.name,
      columnId: column.id,
      variableName: columnNameToFormulaVariable(column.name),
    }));
}
